// --- FILE: src/pages/EmployeeDashboardPage.jsx (ESTILO AJUSTADO A TEMA OSCURO) ---
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useShifts } from '../hooks/useShifts';
import { useShopify } from '../hooks/useShopify';
import { ArrowLeft, DollarSign, BarChart2, AlertTriangle, RefreshCw, Clock, List } from 'lucide-react'; // Eliminado Sun, Moon
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatCurrency, formatDuration, formatDateInPR } from '../utils/formatters';
import { getPayrollWeekBounds } from '../utils/payrollUtils';
import { startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';

// --- Sub-componentes definidos ANTES de ser usados ---

const ShiftTimer = ({ startTime }) => {
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  useEffect(() => {
    if (!startTime) return;
    const startDate = startTime.toDate ? startTime.toDate() : new Date(startTime);
    const timer = setInterval(() => {
      const diff = new Date() - startDate;
      const h = String(Math.floor(diff / 3600000)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
      setElapsedTime(`${h}:${m}:${s}`);
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);
  return <div className="my-6 text-5xl sm:text-6xl font-mono tracking-tighter text-white tabular-nums">
            {elapsedTime}
         </div>; // Texto blanco para el timer
};

// isDarkMode ya no es necesario como prop, se asume Dark Mode
const SummaryCard = ({ icon: Icon, title, value, subtitle, colorClass = 'text-blue-400' }) => (
  <div className="p-6 rounded-xl bg-white/5 border border-white/10"> {/* Fondos oscuros con borde */}
    <div className="flex items-center gap-3 mb-3">
      <Icon className={`w-6 h-6 ${colorClass}`} />
      <h3 className="text-xl font-semibold text-white">{title}</h3> {/* Texto siempre blanco */}
    </div>
    <p className={`text-3xl font-bold text-white`}>{value}</p> {/* Valor siempre blanco */}
    <p className={`text-sm mt-1 text-gray-400`}>{subtitle}</p> {/* Subtítulo siempre gris */}
  </div>
);

// isDarkMode ya no es necesario como prop, se asume Dark Mode
const ShiftHistoryList = ({ shifts }) => {
  if (!shifts || shifts.length === 0) {
    return (
      <div className="p-6 rounded-xl text-center bg-gray-800">
        <p className="text-gray-400">No hay turnos completados en este período.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {shifts.map(shift => (
        <div key={shift.id} className="p-4 rounded-lg bg-white/5 border border-white/10"> {/* Fondo oscuro con borde */}
          <div className="flex justify-between items-center">
            <div>
              <p className="font-semibold text-white">{formatDateInPR(shift.startTime, 'EEEE d MMM')}</p> {/* Texto siempre blanco */}
              <p className="text-sm text-gray-400">
                {formatDateInPR(shift.startTime, 'h:mm a')} - {shift.endTime ? formatDateInPR(shift.endTime, 'h:mm a') : 'Activo'}
              </p>
            </div>
            <div className="text-right">
              <p className="font-bold text-green-400">{formatCurrency(shift.earnings)}</p>
              <p className="text-sm text-gray-400">{formatDuration(shift.durationMinutes / 60)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// --- Componente Principal ---

const EmployeeDashboardPage = () => {
  const { activeEmployee, employeeLogout } = useAuth();
  const { isOnShift, activeShift, startShift, endShift, shiftHistory, periodEarnings, fetchShiftHistory, loading: shiftLoading, error: shiftError } = useShifts();
  const { employeeSalesData, loading: salesLoading, error: salesError, fetchReportForDateRange } = useShopify();
  
  // Eliminado isDarkMode state y su setter
  const [period, setPeriod] = useState('week');

  const handlePeriodChange = useCallback((newPeriod) => {
    setPeriod(newPeriod);
    let startDate, endDate;
    const now = new Date();

    if (newPeriod === 'week') {
      const { weekStart, weekEnd } = getPayrollWeekBounds(now);
      startDate = weekStart;
      endDate = weekEnd;
    } else if (newPeriod === 'today') {
      startDate = startOfDay(now);
      endDate = endOfDay(now);
    } else if (newPeriod === 'month') {
      startDate = startOfMonth(now);
      endDate = endOfMonth(now);
    }
    if (startDate && endDate) {
        fetchShiftHistory(startDate, endDate);
        fetchReportForDateRange(startDate, endDate);
    }
  }, [fetchShiftHistory, fetchReportForDateRange]);
  
  useEffect(() => {
    handlePeriodChange('week');
  }, [handlePeriodChange]);

  const handleClockInOut = async () => {
    if (isOnShift) await endShift();
    else await startShift();
  };

  const totalPeriodEarnings = (periodEarnings || 0) + (employeeSalesData?.commission || 0);
  // Definición de themeClasses simplificada, asumiendo siempre dark mode
  const themeClasses = { 
    bg: 'bg-gradient-to-br from-gray-900 via-black to-gray-900', // Gradiente como en AdminReportPage
    text: 'text-white', 
    button: 'bg-white/10 hover:bg-white/20', // Botones más sutiles
    primaryButton: isOnShift ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700' 
  };
  
  if (!activeEmployee) {
    setTimeout(() => employeeLogout(), 100);
    return <LoadingSpinner fullScreen text="No hay empleado activo..." />;
  }

  const totalLoading = shiftLoading || salesLoading;
  const displayError = shiftError || salesError;

  return (
    <div className={`flex flex-col min-h-[100dvh] px-4 py-4 sm:p-6 transition-colors duration-300 ${themeClasses.bg} ${themeClasses.text}`}>
      <header className="w-full flex justify-between items-center mb-6 sm:mb-8 pt-safe">
        <button onClick={employeeLogout} className={`flex items-center gap-2 px-3 py-2 sm:px-4 rounded-lg text-sm sm:text-base ${themeClasses.button}`}><ArrowLeft size={18} /> Cambiar Empleado</button>
        <div className="flex items-center gap-3">
          <button onClick={() => handlePeriodChange(period)} disabled={totalLoading} className={`p-2 rounded-full ${themeClasses.button}`} title="Refrescar datos"><RefreshCw size={20} className={totalLoading ? 'animate-spin' : ''} /></button>
        </div>
      </header>

      <main className="flex-grow flex flex-col items-center justify-center text-center px-2">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center text-3xl sm:text-4xl font-bold mb-4 bg-blue-500 text-white">
          {activeEmployee.name.charAt(0)}
        </div>
        <h1 className="text-2xl sm:text-4xl font-bold text-white">{activeEmployee.name.split(' ')[0]}</h1>
        <p className="mt-2 text-base sm:text-lg text-gray-400">{isOnShift ? 'Turno en progreso' : 'Listo para iniciar turno'}</p>
        {isOnShift && activeShift && <ShiftTimer startTime={activeShift.startTime} />}
        {displayError && <div className="mt-4 p-3 rounded-lg bg-red-500/20 text-red-300 border border-red-400 flex items-center gap-2"><AlertTriangle size={18} /><span className="text-sm">{displayError}</span></div>}
        <div className="mt-8 sm:mt-12 w-full max-w-md">
          <button onClick={handleClockInOut} disabled={shiftLoading} className={`w-full py-5 sm:py-6 rounded-xl text-xl sm:text-2xl font-semibold text-white shadow-lg ${themeClasses.primaryButton} disabled:opacity-50`}>
            {shiftLoading ? <LoadingSpinner text="Procesando..." /> : (isOnShift ? 'Finalizar Turno' : 'Iniciar Turno')}
          </button>
        </div>
      </main>

      <footer className="w-full max-w-6xl mx-auto mt-8 sm:mt-12 pb-safe">
        {activeEmployee.history !== false && (
          <div className="flex justify-center items-center gap-2 mb-4 sm:mb-6 flex-wrap">
            {['week', 'today', 'month'].map(p => (<button key={p} onClick={() => handlePeriodChange(p)} className={`px-3 py-2 sm:px-4 rounded-lg text-sm font-semibold transition-colors ${period === p ? 'bg-blue-600 text-white' : 'bg-white/10 hover:bg-white/20'}`}>{p === 'week' ? 'Semana Actual' : p === 'today' ? 'Hoy' : 'Mes Actual'}</button>))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <SummaryCard icon={DollarSign} title="Ganancias Totales" value={formatCurrency(totalPeriodEarnings)} subtitle="Salario de turnos + Comisiones" colorClass="text-green-400" />
          {activeEmployee.history !== false && (
            <SummaryCard icon={Clock} title="Ganancias por Turnos" value={formatCurrency(periodEarnings)} subtitle={`${shiftHistory.length} turnos completados`} colorClass="text-purple-400" />
          )}
          {activeEmployee.hasCommission ? (
            <SummaryCard icon={BarChart2} title="Comisiones del Período" value={formatCurrency(employeeSalesData?.commission || 0)} subtitle={`${employeeSalesData?.orders || 0} órdenes con comisión`} colorClass="text-blue-400" />
          ) : (
            <SummaryCard icon={BarChart2} title="Ventas del Período" value={formatCurrency(employeeSalesData?.sales || 0)} subtitle={`${employeeSalesData?.orders || 0} órdenes procesadas`} colorClass="text-blue-400" />
          )}
        </div>
        {activeEmployee.history !== false && (
          <div className="mt-8">
              <div className="flex items-center gap-3 mb-4"><List className="w-6 h-6 text-gray-400"/><h3 className="text-2xl font-semibold text-white">Historial de Turnos</h3></div> {/* Texto siempre blanco */}
              <ShiftHistoryList shifts={shiftHistory} />
          </div>
        )}
      </footer>
    </div>
  );
};

export default EmployeeDashboardPage;